import React, { useState, useMemo } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiX, FiBriefcase } from 'react-icons/fi';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { useClients, useCreateClient, useUpdateClient, useDeleteClient } from '../hooks/useClients';

// Single modal component for both add and edit
const ClientFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialData = null, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    logo: null
  });

  const isEdit = Boolean(initialData);

  // Reset form when modal opens/closes or initialData changes
  React.useEffect(() => {
    if (isOpen) {
      console.log('Modal opened with initialData:', initialData);
      setFormData({
        name: initialData?.name || '',
        logo: null // Always reset logo to null, user needs to select new file
      });
    }
  }, [isOpen, initialData]);

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    const newValue = files ? files[0] : value;
    console.log('Form field changed:', name, 'value:', newValue);
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit(formData, isEdit);
    // Reset form after successful submission
    setFormData({ name: '', logo: null });
  };

  if (!isOpen) return null;

  console.log('Modal rendering with formData:', formData, 'isEdit:', isEdit, 'initialData:', initialData);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            {isEdit ? 'Edit Client' : 'Add New Client'}
          </h3>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700"
            disabled={isLoading}
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              required
              disabled={isLoading}
              placeholder="Enter client name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Logo Image
            </label>
            <input
              type="file"
              name="logo"
              accept="image/*"
              onChange={handleFormChange}
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            />
            {formData.logo && (
              <div className="mt-2">
                <p className="text-sm text-gray-600">
                  Selected: {formData.logo.name}
                </p>
                <p className="text-xs text-gray-500">
                  Size: {(formData.logo.size / 1024).toFixed(2)} KB
                </p>
              </div>
            )}
            {isEdit && initialData?.logo && !formData.logo && (
              <div className="mt-2 flex items-center space-x-2">
                <img 
                  src={initialData.logo} 
                  alt="Current logo" 
                  className="h-8 w-8 object-contain rounded"
                />
                <p className="text-sm text-gray-600">Current logo (select new file to replace)</p>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const OurClients = ({ user }) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Fetch clients using React Query
  const {
    data: clientsResponse,
    isLoading,
    error,
    isFetching,
  } = useClients(pagination.current, pagination.pageSize);
  console.log(clientsResponse);

  // Mutations
  const createClientMutation = useCreateClient();
  const updateClientMutation = useUpdateClient();
  const deleteClientMutation = useDeleteClient();

  // Memoized clients data
  const { clientsData, totalRecords } = useMemo(() => {
    if (!clientsResponse) {
      return { clientsData: [], totalRecords: 0 };
    }

    return {
      clientsData: clientsResponse.clients.data || [],
      totalRecords: clientsResponse.clients.total || 0
    };
  }, [clientsResponse]);

  // Check if any mutation is loading
  const isMutating = createClientMutation.isPending || 
                    updateClientMutation.isPending || 
                    deleteClientMutation.isPending;

  // Get error message from mutations or query
  const errorMessage = error?.message || 
                      createClientMutation.error?.message || 
                      updateClientMutation.error?.message || 
                      deleteClientMutation.error?.message;

  // Table columns configuration
  const columns = [
    {
      key: 'id',
      title: 'ID',
      sortable: true,
    },
    {
      key: 'name',
      title: 'Client Name',
      sortable: true,
    },
    {
      key: 'logo',
      title: 'Logo',
      render: (value, row) => (
        <div className="flex items-center">
          {value ? (
            <img 
              src={value} 
              alt={row.name} 
              className="h-10 w-10 object-contain rounded"
            />
          ) : (
            <div className="h-10 w-10 bg-gray-200 rounded flex items-center justify-center">
              <FiBriefcase className="h-5 w-5 text-gray-500" />
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleViewDetails(row)}
            className="text-blue-600 hover:text-blue-800 p-1"
            title="View Details"
            disabled={isMutating}
          >
            <FiEye className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleEditClient(row)}
            className="text-green-600 hover:text-green-800 p-1"
            title="Edit Client"
            disabled={isMutating}
          >
            <FiEdit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDeleteClient(row)}
            className="text-red-600 hover:text-red-800 p-1"
            title="Delete Client"
            disabled={isMutating}
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  // Breadcrumbs
  const breadcrumbs = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Our Clients', href: '/dashboard/our-clients' },
  ];

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  const handlePageSizeChange = (pageSize, current) => {
    setPagination(prev => ({ ...prev, pageSize, current }));
  };

  const handleAddClient = () => {
    setSelectedClient(null);
    setShowFormModal(true);
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setShowFormModal(true);
  };

  const handleViewDetails = (client) => {
    setSelectedClient(client);
    setShowDetailsModal(true);
  };

  const handleDeleteClient = async (client) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete client "${client.name}". This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      background: '#ffffff',
      color: '#333',
      customClass: {
        popup: 'rounded-lg shadow-xl',
        confirmButton: 'bg-[#dc2626] text-white font-bold py-2 px-4 rounded',
        cancelButton: 'bg-[#6b7280] text-white font-bold py-2 px-4 rounded',
      }
    });

    if (result.isConfirmed) {
      try {
        await deleteClientMutation.mutateAsync(client.id);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Client has been deleted successfully.',
          timer: 2000,
          showConfirmButton: false,
          background: '#ffffff',
          color: '#333',
          customClass: {
            popup: 'rounded-lg shadow-xl',
          }
        });
      } catch (error) {
        console.error('Error deleting client:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete client',
          confirmButtonText: 'OK',
        });
      }
    }
  };

  const handleFormSubmit = async (formData, isEdit) => {
    try {
      console.log('Form submission - formData:', formData, 'isEdit:', isEdit);
      
      // Validate form data before submission
      if (!formData.name || !formData.name.trim()) {
        console.log('Validation failed - name is empty or whitespace');
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'The Name field is required.',
          confirmButtonText: 'OK',
        });
        return;
      }

      // Create FormData to handle file upload properly
      const submitData = new FormData();
      submitData.append('name', formData.name.trim());
      
      // Only append logo if a file is selected
      if (formData.logo && formData.logo instanceof File) {
        submitData.append('logo', formData.logo);
      }
      
      // For Laravel applications, add _method field for PUT requests
      if (isEdit) {
        submitData.append('_method', 'PUT');
      }
      
      // Debug: Log FormData contents
      console.log('FormData contents:');
      for (let [key, value] of submitData.entries()) {
        console.log(`${key}:`, value);
      }

      if (isEdit) {
        await updateClientMutation.mutateAsync({
          id: selectedClient.id,
          clientData: submitData
        });
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Client updated successfully!',
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        await createClientMutation.mutateAsync(submitData);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Client created successfully!',
          timer: 2000,
          showConfirmButton: false,
        });
      }
      
      // Close modal and reset state
      setShowFormModal(false);
      setSelectedClient(null);
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Handle validation errors from the API
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors)
          .flat()
          .join(', ');
        
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: errorMessages,
          confirmButtonText: 'OK',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to save client',
          confirmButtonText: 'OK',
        });
      }
    }
  };

  const handleCloseFormModal = () => {
    setShowFormModal(false);
    setSelectedClient(null);
  };

  // Details Modal Component
  const ClientDetailsModal = ({ isOpen, onClose, client }) => {
    if (!isOpen || !client) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Client Details</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <FiX className="h-5 w-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-center mb-4">
              {client.logo ? (
                <img 
                  src={client.logo} 
                  alt={client.name} 
                  className="h-20 w-20 object-contain rounded"
                />
              ) : (
                <div className="h-20 w-20 bg-gray-200 rounded flex items-center justify-center">
                  <FiBriefcase className="h-8 w-8 text-gray-500" />
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">ID</label>
                <p className="text-gray-900">{client.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <p className="text-gray-900">{client.name}</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Created At</label>
                <p className="text-gray-900">
                  {client.created_at ? new Date(client.created_at).toLocaleString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Handle loading state
  if (isLoading) {
    return (
      <Layout user={user}>
        <div className="space-y-0">
          <PageHeader
            title="Our Clients"
            description="Manage client information and logos"
            breadcrumbs={breadcrumbs}
            user={user}
          />
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            <span className="ml-2 text-gray-600">Loading clients...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-0">
        <PageHeader
          title="Our Clients"
          description="Manage client information and logos"
          breadcrumbs={breadcrumbs}
        />

        {/* Add Client Button */}
        <div className="flex justify-end !mb-4">
          <button
            onClick={handleAddClient}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            disabled={isMutating}
          >
            <FiPlus className="h-4 w-4 mr-2" />
            Add Client
          </button>
        </div>

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-800">{errorMessage}</div>
          </div>
        )}

        <DataTable
          data={clientsData}
          columns={columns}
          loading={isLoading}
          filterable={false}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalRecords,
          }}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          emptyMessage="No clients found"
        />

        {/* Single Form Modal for both Add and Edit */}
        <ClientFormModal
          isOpen={showFormModal}
          onClose={handleCloseFormModal}
          onSubmit={handleFormSubmit}
          initialData={selectedClient}
          isLoading={isMutating}
        />

        {/* Details Modal */}
        <ClientDetailsModal
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          client={selectedClient}
        />
      </div>
    </Layout>
  );
};

export default OurClients; 